/**
 * Pure helpers that turn ANY adapter's Pipeline into a board: its stages as columns, the
 * items in flight in their stage, and items that just finished resting in the stage they
 * ended in for a short while, so a move into the last column is visible.
 */
import type { Pipeline, PipelineBatch, PipelineCapacity, PipelineCapacityClaim, PipelineItem, PipelineStage, Ts } from '@orgus/protocol';
import { queuePlace } from './queuePlace.ts';

/** A finished item stays on the board this long after it ends. */
export const RECENT_ON_BOARD_MS = 10 * 60_000;
/** At most this many finished items per column. */
export const RECENT_PER_COLUMN = 2;
/**
 * The last column is where work ends up (Landed, Shipped, Done): it lists the most recent
 * items that passed into it, however long ago, so it is never an empty "Nothing here".
 */
export const RECENT_IN_LAST_COLUMN = 5;

export interface BoardCard {
  item: PipelineItem;
  /** In flight (queued or active) versus resting after it finished. */
  finished: boolean;
  /** 1-based place in the queue, for items waiting in the first column only: by priority, then age. */
  position: number | null;
  /**
   * What the card says about its place in its column: "Next, waits for the current batch",
   * "#3 in queue", the lander's own words while it works ("Finding which one fails: ..."),
   * why it waits in a later column, or how it ended.
   */
  note: string | null;
}

export interface BoardColumn {
  stage: PipelineStage;
  cards: BoardCard[];
  /** In-flight count (finished cards excluded). */
  inFlight: number;
  /** Finished items resting here. */
  resting: number;
  /** Any card here is active. */
  active: boolean;
  /** The adapter's last stage, where work ends up (Landed): it lists recent arrivals, not work in flight. */
  last: boolean;
}

export const OTHER_STAGE: PipelineStage = { id: '__other', title: 'Other' };

const FINISHED = new Set<PipelineItem['state']>(['passed', 'failed', 'cancelled']);

export function isFinished(i: Pick<PipelineItem, 'state'>): boolean {
  return FINISHED.has(i.state);
}

/** Queue order: active first, then priority, then oldest first. */
export function queueOrder(a: PipelineItem, b: PipelineItem): number {
  const rank = (i: PipelineItem) => (i.state === 'active' ? 0 : i.priority ? 1 : 2);
  return rank(a) - rank(b) || (a.startedAt ?? 0) - (b.startedAt ?? 0) || a.id.localeCompare(b.id);
}

/** What one unit of work is called in this adapter's words: the landing queue verifies batches. */
export function workWord(p: Pick<Pipeline, 'adapter'>): string {
  return p.adapter === 'qawl-land' ? 'batch' : 'run';
}

/** The note a card carries in its column (see BoardCard.note). */
export function cardNote(item: PipelineItem, position: number | null, finished: boolean, busy: boolean, word: string, batch?: PipelineBatch): string | null {
  if (finished) return item.detail ?? null;
  if (item.state === 'active') return item.detail ?? null;
  // With the runner's batch known, the next item says how big the batch it waits for is.
  if (position === 1 && batch) return queuePlace(1, batch);
  if (position != null) return position === 1 ? (busy ? `Next, waits for the current ${word}` : 'Next') : `#${position} in queue`;
  return item.detail ?? `Waits for the current ${word}`;
}

export function boardColumns(p: Pipeline, now: Ts): BoardColumn[] {
  const known = new Set(p.stages.map((s) => s.id));
  const stages = [...p.stages];
  const inFlightIds = new Set(p.items.map((i) => i.id));
  const strays = p.items.some((i) => !known.has(i.stage));
  if (strays) stages.push(OTHER_STAGE);
  const colOf = (i: PipelineItem) => (known.has(i.stage) ? i.stage : OTHER_STAGE.id);
  const busy = p.items.some((i) => i.state === 'active');
  const word = workWord(p);

  // Places are numbered in the first column only: an item waiting further on (a split
  // batch's second half) is not "in the queue" and says why it waits instead.
  const first = p.stages[0]?.id;
  const queued = p.items.filter((i) => i.state === 'queued' && i.stage === first).sort(queueOrder);
  const position = new Map(queued.map((i, n) => [i.id, n + 1]));

  const lastStage = p.stages[p.stages.length - 1]?.id;
  return stages.map((stage) => {
    const live = p.items
      .filter((i) => colOf(i) === stage.id)
      .sort(queueOrder)
      .map((item): BoardCard => {
        const finished = isFinished(item);
        const at = position.get(item.id) ?? null;
        return { item, finished, position: at, note: cardNote(item, at, finished, busy, word, p.batch) };
      });
    const isLast = stage.id === lastStage;
    const resting = p.recent
      .filter(
        (i) =>
          !inFlightIds.has(i.id) &&
          known.has(i.stage) &&
          i.stage === stage.id &&
          (isLast ? i.state === 'passed' || (i.endedAt != null && now - i.endedAt < RECENT_ON_BOARD_MS) : i.endedAt != null && now - i.endedAt < RECENT_ON_BOARD_MS),
      )
      .sort((a, b) => (b.endedAt ?? 0) - (a.endedAt ?? 0))
      .slice(0, isLast ? RECENT_IN_LAST_COLUMN : RECENT_PER_COLUMN)
      .map((item): BoardCard => ({ item, finished: true, position: null, note: cardNote(item, null, true, busy, word) }));
    const cards = [...live, ...resting];
    const inFlight = live.filter((c) => !c.finished).length;
    return { stage, cards, inFlight, resting: cards.length - inFlight, active: live.some((c) => c.item.state === 'active'), last: isLast };
  });
}

/** "Waiting for CPU" reads "waiting for CPU" mid-sentence: only the first letter drops its capital. */
function lowerFirst(s: string): string {
  return s.length > 1 && s[1] === s[1]!.toUpperCase() && /[A-Z]/.test(s[1]!) ? s : s.charAt(0).toLowerCase() + s.slice(1);
}

/**
 * The header's one line about what is in flight, column by column, so it always matches the
 * board: "3 queued, 5 verifying". The last column (where work ends up) is not in flight.
 */
export function flightSummary(cols: readonly BoardColumn[]): string {
  const parts = cols
    .filter((c) => !c.last && c.inFlight > 0)
    .map((c) => `${c.inFlight} ${lowerFirst(c.stage.title)}`);
  return parts.length ? parts.join(', ') : 'Nothing in flight';
}

/** A column's count and what it counts, in words for its tooltip and screen readers. */
export function columnCount(col: BoardColumn): { n: number; label: string } {
  const n = col.cards.length;
  if (col.last) return { n, label: `${n} recent in ${col.stage.title.toLowerCase()}` };
  const parts = [`${col.inFlight} in flight`];
  if (col.resting) parts.push(`${col.resting} finished in the last ${RECENT_ON_BOARD_MS / 60_000} minutes`);
  return { n, label: parts.join(', ') };
}

export interface Outcomes {
  passed: number;
  failed: number;
  cancelled: number;
  total: number;
}

export function outcomes(recent: PipelineItem[]): Outcomes {
  const o: Outcomes = { passed: 0, failed: 0, cancelled: 0, total: 0 };
  for (const i of recent) {
    if (i.state === 'passed') o.passed++;
    else if (i.state === 'failed') o.failed++;
    else if (i.state === 'cancelled') o.cancelled++;
    else continue;
    o.total++;
  }
  return o;
}

export interface CapacityView {
  used: number;
  total: number;
  free: number;
  /** used / total, at most 1, for a gauge. */
  fraction: number;
  full: boolean;
  /** "30 of 30 test workers busy". */
  busy: string;
  /** "1 verify run", when the adapter counts runs. */
  runs: string | null;
  /** "30 of 30 test workers busy, 1 verify run". */
  summary: string;
  /** "18 tasks waiting for a worker", "11 free", or "Full". */
  detail: string;
}

const count = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/**
 * What an adapter's capacity means, in words. A pool shared by more than this pipeline
 * (Qawl's verify pool serves every verify run on the machine) can report more in use
 * than its size for a moment; it is shown as full, never as a negative free count.
 */
export function capacityView(cap: Pick<PipelineCapacity, 'used' | 'total' | 'waiting' | 'label' | 'runs' | 'runLabel'>): CapacityView {
  const total = Math.max(0, Math.floor(cap.total));
  const used = Math.max(0, Math.floor(cap.used));
  const free = Math.max(0, total - used);
  const full = total > 0 && used >= total;
  const label = cap.label.toLowerCase();
  const busy = `${Math.min(used, total)} of ${total} ${label} busy`;
  const runs = cap.runs != null && cap.runs > 0 ? count(cap.runs, cap.runLabel || 'run') : null;
  return {
    used,
    total,
    free,
    fraction: total > 0 ? Math.min(1, used / total) : 0,
    full,
    busy,
    runs,
    summary: runs ? `${busy}, ${runs}` : busy,
    detail: cap.waiting > 0 ? `${count(cap.waiting, 'task')} waiting for a worker` : full ? 'Full' : `${free} free`,
  };
}

export interface ClaimGroup {
  label: string;
  slots: number;
  /** Claims in the group (one task can claim more than once, for a re-run). */
  claims: number;
  pids: number[];
  run?: string;
  /** The oldest claim's time, when known. */
  since?: Ts;
  /** Every claim in it is a reservation. */
  reserved?: boolean;
}

/** Claims grouped by task label, biggest first: what the busy (or wanted) slots are doing. */
export function claimsByLabel(claims: readonly PipelineCapacityClaim[] | undefined): ClaimGroup[] {
  const by = new Map<string, ClaimGroup>();
  for (const c of claims ?? []) {
    const known = by.get(c.label);
    const g: ClaimGroup = known ?? { label: c.label, slots: 0, claims: 0, pids: [] };
    if (c.reserved && (!known || g.reserved)) g.reserved = true;
    else delete g.reserved;
    g.slots += Math.max(0, c.slots);
    g.claims++;
    if (c.pid != null && !g.pids.includes(c.pid)) g.pids.push(c.pid);
    if (c.run && !g.run) g.run = c.run;
    if (c.since != null) g.since = Math.min(g.since ?? c.since, c.since);
    by.set(c.label, g);
  }
  return [...by.values()].sort((a, b) => b.slots - a.slots || a.label.localeCompare(b.label));
}

export interface RunGroup {
  pid: number | null;
  run?: string;
  slots: number;
  tasks: number;
  waiting: number;
  since?: Ts;
}

/** Claims grouped by the process that made them: one row per run. Reservations are not runs. */
export function claimsByRun(holders: readonly PipelineCapacityClaim[] | undefined, queue: readonly PipelineCapacityClaim[] | undefined): RunGroup[] {
  const by = new Map<string, RunGroup>();
  holders = holders?.filter((h) => !h.reserved);
  const at = (c: PipelineCapacityClaim) => {
    const k = c.pid != null ? String(c.pid) : `?${c.run ?? ''}`;
    const g = by.get(k) ?? { pid: c.pid ?? null, slots: 0, tasks: 0, waiting: 0 };
    if (c.run && !g.run) g.run = c.run;
    if (c.since != null) g.since = Math.min(g.since ?? c.since, c.since);
    by.set(k, g);
    return g;
  };
  for (const c of holders ?? []) {
    const g = at(c);
    g.slots += Math.max(0, c.slots);
    g.tasks++;
  }
  for (const c of queue ?? []) at(c).waiting++;
  return [...by.values()].sort((a, b) => b.slots - a.slots || b.waiting - a.waiting);
}

/**
 * An item's age in words: time in its current stage or run first, then, when it began
 * well before that, time since it was submitted. "9 min in this run, 3 h 14 min since submitted".
 */
export function itemAge(
  item: Pick<PipelineItem, 'state' | 'startedAt' | 'stageStartedAt' | 'endedAt'>,
  now: Ts,
  format: (ms: number) => string,
): { stage: number; total: number | null; label: string } | null {
  if (item.startedAt == null && item.stageStartedAt == null) return null;
  const end = item.endedAt ?? now;
  const from = item.stageStartedAt ?? item.startedAt ?? end;
  const stage = Math.max(0, end - from);
  const total = item.startedAt != null && item.stageStartedAt != null && item.stageStartedAt - item.startedAt >= 60_000 ? Math.max(0, end - item.startedAt) : null;
  const where = item.endedAt != null ? '' : item.state === 'active' ? ' in this run' : item.stageStartedAt != null ? ' in this stage' : ' since submitted';
  const label = total != null ? `${format(stage)}${where}, ${format(total)} since submitted` : `${format(stage)}${where}`;
  return { stage, total, label };
}

/** Every item of a pipeline by id (in flight and recent). */
export function findItem(p: Pipeline, id: string): PipelineItem | undefined {
  return p.items.find((i) => i.id === id) ?? p.recent.find((i) => i.id === id);
}

export function stageTitle(p: Pipeline, stageId: string): string {
  return p.stages.find((s) => s.id === stageId)?.title ?? stageId;
}

/** The run name the Qawl adapter gives the landing queue's own claims. */
const GATE_RUN = 'landing queue';

export interface CapacityOwner {
  key: 'gate' | 'others' | 'kept' | 'free';
  /** "Landing queue verify", "2 other verify runs", "Kept free for the landing queue", "Free". */
  label: string;
  /** The item the gate's verify is testing, or the batch, in words. */
  detail: string | null;
  slots: number;
}

export interface CapacityOwners {
  /** Who holds the pool, biggest first, then what is kept and what is free. Empty when holders are not reported. */
  owners: CapacityOwner[];
  /** Workers the landing queue's verify holds, by the item it is testing (the active items). */
  byItem: Map<string, number>;
  /** The gate's tasks, biggest first, for a card's tooltip. */
  gateTasks: ClaimGroup[];
  /** How many distinct tasks hold workers. */
  tasks: number;
  /** More than one item shares the gate's workers (a batch). */
  shared: boolean;
  /** "24 kept for the landing queue, all in use by its verify", when the adapter reports a reservation. */
  reserveNote: string | null;
}

/**
 * Who holds a pipeline's workers, in words a person can act on: the landing queue's own
 * verify (and the item it is testing, so the board can mark that card), other verify runs
 * on the machine, slots kept idle for the landing queue, and what is free.
 */
export function capacityOwners(p: Pick<Pipeline, 'capacity' | 'items' | 'batch'>): CapacityOwners {
  const cap = p.capacity;
  const out: CapacityOwners = { owners: [], byItem: new Map(), gateTasks: [], tasks: 0, shared: false, reserveNote: null };
  if (!cap?.holders) return out;
  const work = cap.holders.filter((h) => !h.reserved);
  const gate = work.filter((h) => h.run === GATE_RUN);
  const others = work.filter((h) => h.run !== GATE_RUN);
  const sum = (cs: readonly PipelineCapacityClaim[]) => cs.reduce((n, c) => n + Math.max(0, c.slots), 0);
  const gateSlots = sum(gate);
  const otherSlots = sum(others);
  const kept = sum(cap.holders.filter((h) => h.reserved));
  out.gateTasks = claimsByLabel(gate);
  out.tasks = claimsByLabel(work).length;
  // The lander verifies one batch at a time: its claims are the work of the items being
  // verified now (an item already pushing or deploying holds no test workers).
  const atWork = p.items.filter((i) => i.state === 'active');
  const verifying = atWork.filter((i) => /verif/i.test(i.stage));
  const active = verifying.length ? verifying : atWork.filter(() => !atWork.some((i) => /push|deploy/i.test(i.stage)));
  out.shared = active.length > 1;
  if (gateSlots > 0) {
    const batch = p.batch && p.batch.size > 1 ? `the current batch, ${count(p.batch.size, p.batch.unit ?? 'item')}` : null;
    const detail = active.length === 1 ? active[0]!.title : (batch ?? (active.length > 1 ? `the current batch, ${count(active.length, 'submission')}` : null));
    out.owners.push({ key: 'gate', label: 'Landing queue verify', detail, slots: gateSlots });
    for (const i of active) out.byItem.set(i.id, gateSlots);
  }
  if (otherSlots > 0) {
    const runs = new Set(others.map((c) => c.pid ?? -1)).size;
    out.owners.push({ key: 'others', label: runs === 1 ? 'Another verify run' : `${runs} other verify runs`, detail: null, slots: otherSlots });
  }
  out.owners.sort((a, b) => b.slots - a.slots);
  if (kept > 0) out.owners.push({ key: 'kept', label: 'Kept free for the landing queue', detail: null, slots: kept });
  const free = Math.max(0, cap.total - cap.used);
  if (free > 0) out.owners.push({ key: 'free', label: 'Free', detail: null, slots: free });
  if (cap.reserve && cap.reserve.slots > 0) {
    const r = cap.reserve;
    const use = r.inUse >= r.slots ? 'all in use by its verify' : r.inUse > 0 ? `${r.inUse} in use by its verify` : 'none in use yet';
    out.reserveNote = `${count(r.slots, 'worker')} kept for ${r.for}, ${use}. Other runs share the rest.`;
  }
  return out;
}
